import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { ProjectDoc } from 'common';
import { User } from 'src/global/models';
import { ProjectService } from 'src/projects/services/ProjectService';
import { SubmittalUtils } from '..';

@Component({
  tag: 'submittal-type-selector',
})
export class SubmittalTypeSelector {
  /**
   * Resolves with the confirmed type, `null` if cleared, or `undefined` if dismissed.
   */
  static async present(projectId: string, selectedType?: string): Promise<string | null | undefined> {
    const modal = await modalController.create({
      component: 'submittal-type-selector',
      componentProps: { projectId, selectedType },
      backdropDismiss: false,
      cssClass: 'modal-height-auto',
    });
    return new Promise(async resolve => {
      modal.addEventListener('selected', (e: CustomEvent<string | null>) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(undefined);
      });
      await modal.present();
    });
  }

  @Prop() projectId: string;
  @Prop() selectedType?: string;

  @State() types: string[] = [];
  @State() currentSelection: string | null = null;
  @State() isAdding: boolean = false;
  @State() newName: string = '';
  @State() isSaving: boolean = false;

  @Event() selected: EventEmitter<string | null>;
  @Event() dismiss: EventEmitter<void>;

  private project: ProjectDoc;

  get user(): User {
    return globalThis.user;
  }

  get canWrite(): boolean {
    return this.user.getSubmittalsPermissions(this.projectId).canWrite;
  }

  async componentWillLoad() {
    this.currentSelection = this.selectedType || null;

    const db = ProjectService.getProjectDb(this.projectId);
    const doc: ProjectDoc = await db.getDoc(this.projectId);
    if (doc) {
      this.handleProjectDoc(doc);
    } else {
      db.addListener('complete', docs => this.handleProjectDoc(docs[0] as ProjectDoc));
    }
  }

  private handleProjectDoc(doc: ProjectDoc) {
    this.project = doc;
    this.types = doc.submittals_types || SubmittalUtils.DEFAULT_SUBMITTAL_TYPES;
  }

  private toggleType(type: string) {
    this.currentSelection = this.currentSelection === type ? null : type;
  }

  private enterAddMode() {
    this.isAdding = true;
    this.newName = '';
  }

  private exitAddMode() {
    this.isAdding = false;
    this.newName = '';
  }

  private handleConfirm() {
    this.selected.emit(this.currentSelection);
  }

  private async handleAdd() {
    const name = this.newName.trim();
    if (!name || this.isSaving) return;

    const existing = this.types.find(type => type.toLowerCase() === name.toLowerCase());
    if (existing) {
      this.selected.emit(existing);
      return;
    }

    this.isSaving = true;
    try {
      if (!this.project.submittals_types || this.project.submittals_types.length === 0) {
        this.project.submittals_types = [...SubmittalUtils.DEFAULT_SUBMITTAL_TYPES];
      }
      this.project.submittals_types.push(name);
      this.project = await ProjectService.saveProjectDoc(this.project);
      this.types = this.project.submittals_types;
      this.selected.emit(name);
    } finally {
      this.isSaving = false;
    }
  }

  render() {
    return (
      <Host class="concourse-modal">
        <modal-header modalTitle={this.isAdding ? 'Add Type' : 'Select Type'} showBack={this.isAdding} onBack={() => this.exitAddMode()} onDismiss={() => this.dismiss.emit()} />
        {this.isAdding ? (
          <div class="modal-content cc-padding">
            <ion-input
              label="Type name"
              label-placement="stacked"
              placeholder="Enter type name"
              value={this.newName}
              autofocus
              onIonInput={e => (this.newName = (e.target as HTMLIonInputElement).value as string)}
            />
          </div>
        ) : (
          <div class="modal-content cc-scrollbars">
            <ion-list class="cc-no-line-list transparent-background" style={{ marginBottom: '30px' }}>
              {this.types.length === 0 ? (
                <ion-text class="ion-padding-horizontal">No types available.</ion-text>
              ) : (
                this.types.map(type => (
                  <selector-item selected={this.currentSelection === type} showCheckmark={true} onItemClick={() => this.toggleType(type)}>
                    {type}
                  </selector-item>
                ))
              )}
            </ion-list>
          </div>
        )}
        <ion-footer>
          <ion-toolbar>
            {!this.isAdding && this.canWrite && (
              <ion-button slot="start" shape="round" fill="outline" onClick={() => this.enterAddMode()}>
                Add type
              </ion-button>
            )}
            {this.isAdding ? (
              <ion-button slot="end" shape="round" color="primary" fill="solid" disabled={!this.newName.trim() || this.isSaving} onClick={() => this.handleAdd()}>
                Add {this.newName.trim()}
              </ion-button>
            ) : (
              <ion-button slot="end" shape="round" color="primary" fill="solid" onClick={() => this.handleConfirm()}>
                {this.currentSelection ? `Select ${this.currentSelection}` : 'Select None'}
              </ion-button>
            )}
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
